import { RTC_PROVIDERS } from '../../../providerIds'
import {
  createDailyRtcCompatibilityProps,
  mergeDailyRtcCompatibilityProps,
  preloadDailyRtcModule,
} from './dailyRtcCompat'

function createEventBus() {
  const listeners = new Map()

  return {
    emit(eventName, payload) {
      const handlers = listeners.get(eventName)
      if (!handlers) {
        return
      }

      handlers.forEach((handler) => handler(payload))
    },
    on(eventName, handler) {
      const handlers = listeners.get(eventName) || new Set()
      handlers.add(handler)
      listeners.set(eventName, handlers)

      return () => {
        const nextHandlers = listeners.get(eventName)
        if (!nextHandlers) {
          return
        }

        nextHandlers.delete(handler)
        if (nextHandlers.size === 0) {
          listeners.delete(eventName)
        }
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

export const DAILY_RTC_SESSION_EVENTS = Object.freeze({
  configChanged: 'config-changed',
  preloadRequested: 'preload-requested',
  joinRequested: 'join-requested',
  leaveRequested: 'leave-requested',
  audioToggleRequested: 'audio-toggle-requested',
  videoToggleRequested: 'video-toggle-requested',
  cameraSwitchRequested: 'camera-switch-requested',
  microphoneSwitchRequested: 'microphone-switch-requested',
  playbackDeviceSwitchRequested: 'playback-device-switch-requested',
  screenshareStartRequested: 'screenshare-start-requested',
  screenshareStopRequested: 'screenshare-stop-requested',
  destroyed: 'destroyed',
})

export function createDailyRtcSessionHandle({ initialConfig = {} } = {}) {
  const events = createEventBus()
  let compatibilityProps = createDailyRtcCompatibilityProps(initialConfig)
  let destroyed = false

  function assertActive(methodName) {
    if (!destroyed) {
      return
    }

    throw new Error(`[live/rtc/daily] ${methodName}() called after destroy()`)
  }

  function updateCompatibilityProps(nextConfig) {
    compatibilityProps = mergeDailyRtcCompatibilityProps(compatibilityProps, nextConfig)
    events.emit(DAILY_RTC_SESSION_EVENTS.configChanged, compatibilityProps)
    return compatibilityProps
  }

  return {
    providerId: RTC_PROVIDERS.dailyCo,
    getConfig() {
      return compatibilityProps
    },
    updateConfig(nextConfig = {}) {
      assertActive('updateConfig')
      return updateCompatibilityProps(nextConfig)
    },
    async preload() {
      assertActive('preload')
      events.emit(DAILY_RTC_SESSION_EVENTS.preloadRequested, compatibilityProps)
      await preloadDailyRtcModule()
    },
    async join() {
      assertActive('join')
      await preloadDailyRtcModule()
      events.emit(DAILY_RTC_SESSION_EVENTS.joinRequested, compatibilityProps)
      return compatibilityProps
    },
    async leave() {
      assertActive('leave')
      events.emit(DAILY_RTC_SESSION_EVENTS.leaveRequested, compatibilityProps)
      return true
    },
    async setAudioEnabled(enabled) {
      assertActive('setAudioEnabled')
      updateCompatibilityProps({
        rtcProps: {
          enableAudio: Boolean(enabled),
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.audioToggleRequested, Boolean(enabled))
      return compatibilityProps.rtcProps.enableAudio
    },
    async setVideoEnabled(enabled) {
      assertActive('setVideoEnabled')
      updateCompatibilityProps({
        rtcProps: {
          enableVideo: Boolean(enabled),
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.videoToggleRequested, Boolean(enabled))
      return compatibilityProps.rtcProps.enableVideo
    },
    async switchCamera(deviceId) {
      assertActive('switchCamera')
      updateCompatibilityProps({
        rtcProps: {
          cameraDeviceId: deviceId,
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.cameraSwitchRequested, deviceId)
      return deviceId
    },
    async switchMicrophone(deviceId) {
      assertActive('switchMicrophone')
      updateCompatibilityProps({
        rtcProps: {
          microphoneDeviceId: deviceId,
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.microphoneSwitchRequested, deviceId)
      return deviceId
    },
    async setPlaybackDevice(deviceId) {
      assertActive('setPlaybackDevice')
      updateCompatibilityProps({
        rtcProps: {
          playbackDeviceId: deviceId,
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.playbackDeviceSwitchRequested, deviceId)
      return deviceId
    },
    async startScreenshare() {
      assertActive('startScreenshare')
      updateCompatibilityProps({
        rtcProps: {
          enableScreensharing: true,
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.screenshareStartRequested, compatibilityProps)
      return true
    },
    async stopScreenshare() {
      assertActive('stopScreenshare')
      updateCompatibilityProps({
        rtcProps: {
          enableScreensharing: false,
        },
      })
      events.emit(DAILY_RTC_SESSION_EVENTS.screenshareStopRequested, compatibilityProps)
      return true
    },
    on(eventName, handler) {
      assertActive('on')
      return events.on(eventName, handler)
    },
    async destroy() {
      if (destroyed) {
        return
      }

      destroyed = true
      events.emit(DAILY_RTC_SESSION_EVENTS.destroyed, compatibilityProps)
      events.clear()
    },
  }
}
